import { Feature } from "@/lib/models";
import axios, { type AxiosProgressEvent } from "axios";
import { axiosWithAuth } from "@/lib/AxiosClient";

export interface AttachmentDetails {
  id: string
  filename: string
  entityId: string
  url: string
  thumbnailUrl: string
  size: number
  uploadDate: string
  type: AttachmentType
  originalFilename: string
  dateTaken: string | null
  createdByName: string
}

export const AttachmentType = {
  None: 0,
  Main: 1,
} as const;

export type AttachmentType = 0 | 1;

export interface NewFeatureRequest extends Feature {
  siteId: string
}

export type AttachmentEntityType = 'Observation' | 'Action' | 'Feature';
export type AttachmentSource = 'Comment' | 'Entity';

export interface SasTokenDetails {
  sasToken: string;
  attachmentId: string;
}

export interface SasUploadResponse {
  sasTokens: SasTokenDetails[];
}

export interface SasUploadMetadata {
  originalFilename: string;
  attachmentId: string;
  attachmentType: AttachmentType;
  entityType: AttachmentEntityType;
  source: AttachmentSource;
}

export class AttachmentService {
  private static readonly BASE_PATH = "/api/attachments"
  private static instance = axiosWithAuth;

  static async getByEntityId(entityId: string): Promise<AttachmentDetails[]> {
    const response = await this.instance.get<AttachmentDetails[]>(`${this.BASE_PATH}/${entityId}`);
    return response.data;
  }
  
  static async getOldestByEntityIds(entityIds: string[]): Promise<AttachmentDetails[]> {
    const response = await this.instance.post<AttachmentDetails[]>(`${this.BASE_PATH}/main`, { entityIds: entityIds});
    return response.data;
  }

  static async deleteAttachment(featureId: string, attachmentId: string): Promise<void> {
    const response = await this.instance.delete<void>(`${this.BASE_PATH}/${featureId}/${attachmentId}`);
    return response.data;
  }

  static async delete(attachmentId: string): Promise<void> {
    const response = await this.instance.delete<void>(`${this.BASE_PATH}/${attachmentId}`);
    return response.data;
  }

  static async getSasTokens(filenames: string[], tenantId: string, siteId: string, entityId: string): Promise<SasTokenDetails[]> {
    const response = await this.instance.post<SasUploadResponse>(`${this.BASE_PATH}/sas-upload`, { filenames, tenantId, siteId, entityId });
    return response.data.sasTokens;
  }

  static async uploadToSasUrl(
    sasUrl: string,
    file: File,
    metadata: SasUploadMetadata,
    onUploadProgress: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<void> {
    await axios.put(sasUrl, file, {
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": file.type || "application/octet-stream",
        "x-ms-meta-OriginalFilename": encodeURIComponent(metadata.originalFilename),
        "x-ms-meta-AttachmentId": metadata.attachmentId,
        "x-ms-meta-AttachmentType": metadata.attachmentType,
        "x-ms-meta-EntityType": metadata.entityType,
        "x-ms-meta-Source": metadata.source,
      },
      onUploadProgress,
    });
  }
}
